import React, { useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Heading,
  Icon,
  Tooltip,
  Circle,
  useColorModeValue,
} from "@chakra-ui/react";
import Calendar from "react-calendar";
import {
  FaTasks,
  FaClock,
  FaProjectDiagram,
  FaInfoCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaHourglassHalf,
} from "react-icons/fa";
import { HiOutlineClipboardList } from "react-icons/hi";
import "react-calendar/dist/Calendar.css";
import { IconWrapper } from "../../../components/IconWrapper";

// 🟡 Timesheet Entry Type
interface TimesheetEntry {
  date: string;
  hours: string;
  jobCode: string;
  project: string;
  status: string;
  taskDescription: string;
}

// 🟠 Calendar Props
interface TimesheetCalendarProps {
  timesheets: TimesheetEntry[];
  color: string;
  bg: string;
}

// Add custom SVG clipboard-clock icon
const ClipboardClockIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
    <rect x="9" y="2" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
    <circle cx="12" cy="14" r="4" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M12 12v2l1.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const TimesheetCalendar: React.FC<TimesheetCalendarProps> = ({
  timesheets,
  color,
  bg,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Dynamic theming
  const panelBg = useColorModeValue("white", "gray.700");
  const panelBorder = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("#111827", "#FFFFFF");
  const subText = useColorModeValue("gray.600", "gray.300");
  const gold = "#B59143";

  // react-calendar overrides for visibility in dark mode
  const calText = useColorModeValue("#111827", "#E5E7EB");
  const calMuted = useColorModeValue("#6B7280", "#9CA3AF");
  const calActiveBg = useColorModeValue("rgba(181,145,67,0.10)", "rgba(181,145,67,0.18)");

  const finalBg = bg || panelBg;
  const finalColor = color || textColor;

  // 📌 Find entry for selected date
  const selectedEntry = timesheets.find(
    (entry) => {
      const entryDate = new Date(entry.date);
      return (
        selectedDate &&
        entryDate.getFullYear() === selectedDate.getFullYear() &&
        entryDate.getMonth() === selectedDate.getMonth() &&
        entryDate.getDate() === selectedDate.getDate()
      );
    }
  );

  // 🟢 Function to style dates on the calendar with circular status highlights
  const tileContent = ({ date }: { date: Date }) => {
    const entry = timesheets.find(
      (entry) => {
        const entryDate = new Date(entry.date);
        return (
          entryDate.getFullYear() === date.getFullYear() &&
          entryDate.getMonth() === date.getMonth() &&
          entryDate.getDate() === date.getDate()
        );
      }
    );

    if (!entry) return null;

    return (
      <Circle
        size="22px"
        border="2px solid"
        borderColor="white"
        bg={
          entry.status === "approved"
            ? "green.400"
            : entry.status === "pending"
            ? "yellow.400"
            : "red.400"
        }
        color={color}
        fontSize="xs"
        fontWeight="bold"
        mx="auto"
      >
        {date.getDate()}
      </Circle>
    );
  };

  return (
    <VStack height={"55vh"} spacing={4} align="stretch" p={3}>
      {/* 🟢 Legend Section */}
      <HStack justify="space-between" mb={1} align="center">
        <HStack spacing={4}>
          <Text fontSize="md" fontWeight="bold" color={gold}>
            Status:
          </Text>
          <HStack spacing={2}>
            <Circle size="14px" bg="green.400" />
            <Text fontSize="sm" fontWeight="medium" color={subText}>
              Approved
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Circle size="14px" bg="yellow.400" />
            <Text fontSize="sm" fontWeight="medium" color={subText}>
              Pending
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Circle size="14px" bg="red.400" />
            <Text fontSize="sm" fontWeight="medium" color={subText}>
              Rejected
            </Text>
          </HStack>
        </HStack>

        {/* ℹ️ Info Tooltip */}
        {/* <Tooltip label="Click a date to see timesheet details" fontSize="xs">
          <Icon as={FaInfoCircle} color="gray.500" boxSize={5} />
        </Tooltip> */}
      </HStack>

      {/* 🟢 Calendar with Right-Side Details */}
      <HStack align="flex-start" spacing={5}>
        {/* 📅 Calendar */}
        <Box
          p={2}
          boxShadow="sm"
          bg={finalBg}
          height="350px" // Reduced Height
          width="400px"
          color={finalColor}
          sx={{
            ".react-calendar": {
              background: "transparent !important",
              color: `${calText} !important`,
            },
            ".react-calendar__navigation button": {
              color: `${calText} !important`,
              background: "transparent !important",
            },
            ".react-calendar__navigation__label": {
              color: `${calText} !important`,
            },
            ".react-calendar__navigation__arrow": {
              color: `${calText} !important`,
            },
            ".react-calendar__month-view__weekdays__weekday": {
              color: `${calMuted} !important`,
            },
            ".react-calendar__tile": {
              color: `${calText} !important`,
              background: "transparent !important",
            },
            ".react-calendar__month-view__days__day--weekend": {
              color: `${calText} !important`,
            },
            ".react-calendar__tile--now": {
              background: "transparent !important",
              color: `${calText} !important`,
            },
            ".react-calendar__tile--active": {
              background: `${calActiveBg} !important`,
              color: `${calText} !important`,
            },
          }}
        >
          <Calendar
            tileContent={tileContent}
            onClickDay={(date) => setSelectedDate(date)}
            calendarType="gregory"
            // 🟢 Fix: Hide Default Numbers from Calendar
            tileClassName={({ date }) =>
              timesheets.some(
                (entry) => {
                  const entryDate = new Date(entry.date);
                  return (
                    entryDate.getFullYear() === date.getFullYear() &&
                    entryDate.getMonth() === date.getMonth() &&
                    entryDate.getDate() === date.getDate()
                  );
                }
              )
                ? "hide-default-day"
                : ""
            }
          />
        </Box>

        {/* 🟢 Right-Side Details Panel */}
        <Box
          borderRadius="md"
          boxShadow="md"
          p={5}
          width="320px"
          bg={finalBg}
          border="1px solid"
          borderColor={panelBorder}
        >
          {selectedEntry ? (
            <VStack align="stretch" spacing={3}>
              <Heading fontSize="lg" color={gold} display="flex" alignItems="center" gap={2}>
                Timesheet Details
                <span style={{ color: gold }}>
                  <ClipboardClockIcon style={{ verticalAlign: 'middle', display: 'inline-block' }} />
                </span>
              </Heading>
              <Divider />

              <HStack>
                <Text fontWeight="bold" color={textColor}>📅 Date:</Text>
                <Text color={subText}>{selectedEntry.date}</Text>
              </HStack>
              <HStack>
                <IconWrapper
                  icon={FaClock}
                  size={16}
                  className="text-[#B59143]"
                />
                <Text fontWeight="bold" color={textColor}>Hours Worked:</Text>
                <Text color={subText}>{selectedEntry.hours} hours</Text>
              </HStack>
              <HStack>
                <IconWrapper
                  icon={FaProjectDiagram}
                  size={16}
                  className="text-[#B59143]"
                />
                <Text fontWeight="bold" color={textColor}>Project:</Text>
                <Text color={subText}>{selectedEntry.project}</Text>
              </HStack>
              <HStack>
                <IconWrapper
                  icon={FaTasks}
                  size={16}
                  className="text-[#B59143]"
                />
                <Text fontWeight="bold" color={textColor}>Task:</Text>
                <Text color={subText}>{selectedEntry.taskDescription}</Text>
              </HStack>
              <HStack>
                <HStack spacing={2}>
                  {/* Status Icon Based on Status */}
                  {selectedEntry.status === "approved" && (
                    <IconWrapper
                      icon={FaCheckCircle}
                      size={16}
                      className="text-green-400"
                    />
                  )}
                  {selectedEntry.status === "pending" && (
                    <IconWrapper
                      icon={FaHourglassHalf}
                      size={16}
                      className="text-yellow-400"
                    />
                  )}
                  {selectedEntry.status === "rejected" && (
                    <IconWrapper
                      icon={FaTimesCircle}
                      size={16}
                      className="text-red-400"
                    />
                  )}
                  <Text fontWeight="bold" color={textColor}>Status:</Text>

                  <Badge
                    colorScheme={
                      selectedEntry.status === "approved"
                        ? "green"
                        : selectedEntry.status === "pending"
                        ? "yellow"
                        : "red"
                    }
                    fontSize="0.9em"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                  >
                    {selectedEntry.status.toUpperCase()}
                  </Badge>
                </HStack>
              </HStack>
            </VStack>
          ) : (
            <VStack align="center" spacing={3} mt={3}>
              <IconWrapper
                icon={FaTimesCircle}
                size={32}
                className="text-gray-400"
              />
              <Text fontSize="md" color={subText}>
                No entries for this date.
              </Text>
            </VStack>
          )}
        </Box>
      </HStack>
    </VStack>
  );
};

export default TimesheetCalendar;
